import { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';
import { type Avatar } from '@/components/AvatarStepSelector'; // Import Avatar type

// Deklarera gtag på window-objektet för TypeScript
declare global {
  interface Window {
    gtag?: (command: string, action: string, params?: object) => void;
  }
}

export interface CartItem {
  id: string;
  product_id: string;
  size: string;
  color: string;
  print?: string | null;
  avatar_id?: string | null;
  avatar_name?: string | null; // Added avatar_name field
  quantity: number;
  product: {
    name: string;
    price: number;
    image_url: string | null;
  };
}

interface CartContextType {
  cartItems: CartItem[];
  selectedAvatar: Avatar | null; // Store the full Avatar object
  setSelectedAvatar: (avatar: Avatar | null) => void; // Update setter name and type
  addToCart: (
    productId: string,
    size: string,
    color: string,
    print?: string | null,
    avatar?: Avatar | null
  ) => Promise<void>;
  updateQuantity: (itemId: string, increment: boolean) => Promise<void>;
  isLoading: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_ID_KEY = 'bymatthew_cart_id';

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [cartId, setCartId] = useState<string | null>(null);
  // Renamed the state setter to avoid confusion with the exported function
  const [selectedAvatar, _setSelectedAvatarState] = useState<Avatar | null>(null);

  // Initialize cart ID from localStorage or create new one
  useEffect(() => {
    const initializeCart = async () => {
      try {
        let storedCartId = localStorage.getItem(CART_ID_KEY);

        if (!storedCartId) {
          storedCartId = uuidv4();
          localStorage.setItem(CART_ID_KEY, storedCartId);
        }

        setCartId(storedCartId);
        await fetchCartItems(storedCartId);
      } catch (error) {
        console.error('Error initializing cart:', error);
        toast.error('Failed to initialize cart');
      } finally {
        setIsLoading(false);
      }
    };

    initializeCart();
  }, []);

  const fetchCartItems = async (id: string) => {
    console.log('[CartContext] Fetching cart items for cart ID:', id);
    try {
      const { data, error } = await supabase
        .from('cart_items')
        .select(
          `
          id,
          product_id,
          size,
          color,
          print,
          avatar_id,
          avatar_name,
          quantity,
          cart_id,
          product:products (
            name,
            price,
            image_url
          )
        `
        )
        .eq('cart_id', id);

      if (error) throw error;
      console.log('[CartContext] Fetched cart items:', data);
      setCartItems(data || []);
    } catch (error) {
      console.error('Error fetching cart items:', error);
      toast.error('Failed to fetch cart items');
    }
  };

  const addToCart = async (
    productId: string,
    size: string,
    color: string,
    print?: string | null,
    avatar?: Avatar | null
  ) => {
    console.log('[CartContext] addToCart called with:', { productId, size, color, print, avatar });
    if (!cartId) {
      toast.error('Cart not initialized');
      return;
    }

    try {
      setIsLoading(true);

      // Check if item already exists in cart
      const existingItem = cartItems.find(
        item =>
          item.product_id === productId &&
          item.size === size &&
          item.color === color &&
          item.print === print &&
          item.avatar_id === avatar?.id
      );

      if (existingItem) {
        // Update quantity of existing item
        await updateQuantity(existingItem.id, true);
        return; // Exit early if item updated
      }

      // Prepare cart item data for insertion
      const cartItemData = {
        product_id: productId,
        size,
        color,
        print,
        avatar_id: avatar?.id,
        avatar_name: avatar?.name,
        cart_id: cartId,
        // quantity defaults to 1 in db
      };

      console.log('[CartContext] Inserting cart item with data:', cartItemData);

      // Add new item to cart
      const { data: insertedData, error } = await supabase
        .from('cart_items')
        .insert([cartItemData])
        .select(
          `
          id,
          product_id,
          size,
          color,
          print,
          avatar_id,
          avatar_name,
          quantity,
          cart_id
          `
        )
        .single(); // Expecting only one row back

      if (error) {
        console.error('[CartContext] Error inserting cart item:', error);
        toast.error(`Failed to add item: ${error.message}`);
        throw error; // Rethrow to prevent further execution like fetchCartItems
      }

      // --- NY LOGGNING ---
      console.log('[CartContext] Data returned after insert:', insertedData);
      if (insertedData) {
        console.log('[CartContext] Verification - Saved avatar_id:', insertedData.avatar_id);
        console.log('[CartContext] Verification - Saved avatar_name:', insertedData.avatar_name);
      } else {
        console.warn('[CartContext] No data returned after insert call.');
      }
      // --- SLUT NY LOGGNING ---

      // Refresh cart items from database to update the state
      await fetchCartItems(cartId);
      toast.success('Added to cart');
    } catch (error) {
      // Error already logged or handled by toast
      console.error('Error in addToCart:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateQuantity = async (itemId: string, increment: boolean) => {
    if (!cartId) return;

    try {
      setIsLoading(true);
      const item = cartItems.find(i => i.id === itemId);

      if (!item) return;

      // Send GA4 remove_from_cart event *before* DB operation if decrementing
      if (!increment && typeof window.gtag === 'function') {
        const gaItems = [
          {
            // Always send quantity 1 for the removal event
            item_id: item.product_id,
            item_name: item.product_id, // Using ID as name
            // price: item.product.price, // Minimal: Excluding price
            item_variant:
              [
                item.color,
                item.size,
                item.print ? `Print: ${item.print}` : null,
                // item.avatar_name ? `Avatar: ${item.avatar_name}` : null, // Minimal: Excluding avatar
              ]
                .filter(Boolean)
                .join(' / ') || undefined,
            quantity: 1, // Represents the removal/decrement action itself
          },
        ];

        window.gtag('event', 'remove_from_cart', {
          currency: 'SEK',
          // value: item.product.price, // Minimal: Excluding value
          items: gaItems,
        });
        console.log('[GA4 Event Sent] remove_from_cart (minimal):', { itemId: item.product_id });
      } else if (!increment) {
        console.warn('gtag function not found. Google Analytics event not sent.');
      }

      const newQuantity = increment ? item.quantity + 1 : item.quantity - 1;

      if (newQuantity === 0) {
        // Delete item if quantity would be 0
        const { error } = await supabase
          .from('cart_items')
          .delete()
          .eq('id', itemId)
          .eq('cart_id', cartId);

        if (error) throw error;
      } else {
        // Update quantity
        const { error } = await supabase
          .from('cart_items')
          .update({ quantity: newQuantity })
          .eq('id', itemId)
          .eq('cart_id', cartId);

        if (error) throw error;
      }

      // Refresh cart items
      await fetchCartItems(cartId);
    } catch (error) {
      console.error('Error updating quantity:', error);
      toast.error('Failed to update quantity');
    } finally {
      setIsLoading(false);
    }
  };

  // Custom setter function with logging
  const setSelectedAvatar = (avatar: Avatar | null) => {
    console.log('[CartContext] setSelectedAvatar called with:', avatar);
    _setSelectedAvatarState(avatar);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        selectedAvatar,
        setSelectedAvatar,
        addToCart,
        updateQuantity,
        isLoading,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
